<script setup lang="ts">
import { ref, watch, computed, onMounted } from 'vue';
import { useI18N } from '@dialpad/i18n';
import { useRouter } from 'vue-router';
import { useCampaignStore } from '@/stores';
import { DtModal } from '@dialpad/dialtone/vue3/lib/modal';
import { DtButton } from '@dialpad/dialtone/vue3/lib/button';
import { DtIcon } from '@dialpad/dialtone/vue3/lib/icon';
import StartAndEndTime from '../CampaignEdit/StartAndEndTime/StartAndEndTime.vue';
import type { ModalProps } from '@/app/components/modal/modals';
import { closeModal, updateShow } from '@/app/components/modal/modals';
import CampaignBasicInfo from '../CampaignBasicInfo/CampaignBasicInfo.vue';
import { CampaignAction } from '../CampaignEdit/campaign-edit.constants';
import {
  CampaignPatchActionEnum,
  CampaignStateEnum,
  ResponseError,
} from '@/apis/generated/dialbunny';
import { DATA_QA } from './launch-campaign.constants';
import { DATA_QA as MODAL_WRAPPER_DATA_QA } from '@/app/components/modal/modal-wrapper.constants';
import logger from '@/logger';
import { showAutoDismissToast, showErrorToast } from '@/noto';

const log = logger.getChild('LaunchCampaign');
const { $t } = useI18N();
const router = useRouter();

const props = defineProps<ModalProps>();

const campaignStore = useCampaignStore();

const processing = ref(false);
const disabled = ref(false);
const watchCampaign = campaignStore.watch(
  () => props.meta?.id ?? '',
  'LaunchCampaign',
);
const mounted = ref(false);

onMounted(() => {
  mounted.value = true;
});

const campaignAction = computed(() =>
  watchCampaign.value.item?.dateScheduledStart
    ? CampaignAction.SCHEDULE
    : CampaignAction.LAUNCH,
);

const show = computed(
  () => mounted.value && !!watchCampaign.value.item && !!campaignAction.value,
);

watch(
  () => props.meta?.id,
  async (campaignId) => {
    if (!campaignId) {
      log.error('Missing campaign ID in modal meta');
      void closeModal();
      return;
    }
  },
  { immediate: true },
);

watch(
  [() => watchCampaign.value.item, () => watchCampaign.value.loading],
  ([campaign, loading]) => {
    if (!loading && !campaign) {
      log.log('Campaign is deleted');
      void closeModal();
      // Show toast
    }
  },
);

watch(
  () => watchCampaign.value.item?.state,
  (state) => {
    if (state !== CampaignStateEnum.Pending) {
      log.log('Campaign is not in draft state any more');
      void closeModal();
      // Show toast
    }
  },
);

watch(
  () => watchCampaign.value.error,
  (error) => {
    if (error) {
      log.warn(
        `Error fetching campaign. Status: ${error.response.status}, Message: ${error.message}`,
      );
      void closeModal();
      // Show toast
    }
  },
);

const modalTitle = computed(() => {
  return campaignAction.value === CampaignAction.LAUNCH
    ? $t('LAUNCH_CAMPAIGN_MODAL_TITLE_LAUNCH')
    : $t('LAUNCH_CAMPAIGN_MODAL_TITLE_SCHEDULE');
});

const showBanner = computed(() => {
  return campaignAction.value === CampaignAction.LAUNCH;
});

const processAction = async () => {
  if (!watchCampaign.value.item) return;
  processing.value = true;
  campaignStore.update(
    watchCampaign.value.item.id,
    {
      action: CampaignPatchActionEnum.Launch,
    },
    {
      onSuccess: (updated) => {
        log.debug('Campaign launched');
        void router.push({
          name: 'campaign',
          params: { id: updated.id },
        });
        processing.value = false;
        void showAutoDismissToast({
          content: {
            body: {
              key: `TOAST_LAUNCH_CAMPAIGN_SUCCESS_BODY`,
            },
          },
        });
      },
      onRetry: () => {
        // show toast
      },
      onError: (error) => {
        log.log(`Error ${campaignAction.value} campaign:`, error);
        processing.value = false;
        if (error instanceof ResponseError && error.response.status === 403) {
          void showErrorToast({
            content: {
              body: {
                key: `TOAST_LAUNCH_CAMPAIGN_403_BODY`,
              },
            },
          });
        }
      },
    },
  );
};
</script>
<template>
  <DtModal
    :data-qa="MODAL_WRAPPER_DATA_QA.DIALOG"
    :title="modalTitle"
    :close-button-props="{
      ariaLabel: $t('MODAL_CLOSE_ARIA_LABEL'),
      'data-qa': MODAL_WRAPPER_DATA_QA.CLOSE,
    }"
    :show="show"
    banner-kind="warning"
    content-class="d-p4 d-mn4"
    @update:show="updateShow"
  >
    <template v-if="showBanner" #banner>
      <div :data-qa="DATA_QA.BANNER" class="d-d-flex d-ai-center d-fw-bold">
        <DtIcon
          :data-qa="DATA_QA.BANNER_ICON"
          name="alert-triangle"
          size="400"
          class="d-mr12 d-fc-gold-400"
        />
        <div :data-qa="DATA_QA.BANNER_TITLE">
          {{ $t('LAUNCH_CAMPAIGN_BANNER_TITLE') }}
        </div>
      </div>
    </template>
    <div v-if="watchCampaign.item" :data-qa="DATA_QA.CONTENT" class="d-ml4">
      <CampaignBasicInfo :campaign="watchCampaign.item" />
      <div :data-qa="DATA_QA.DIVIDER" class="d-bb d-bc-default d-my24" />
      <StartAndEndTime
        :campaign="watchCampaign.item"
        :show-section-title="false"
        :show-end-time="false"
        :class="{ 'd-pb24': campaignAction === CampaignAction.SCHEDULE }"
        @update:error="
          (value) => {
            disabled = value.length > 0;
          }
        "
      />
      <div class="d-d-flex d-jc-flex-end d-mt16">
        <DtButton
          :data-qa="DATA_QA.BACK_BUTTON"
          importance="clear"
          @click="closeModal"
        >
          {{ $t('LAUNCH_CAMPAIGN_BACK') }}
        </DtButton>
        <DtButton
          :data-qa="DATA_QA.CONTINUE_BUTTON"
          class="d-ml16"
          :loading="processing"
          :disabled="disabled"
          @click="processAction"
        >
          {{
            campaignAction === CampaignAction.LAUNCH
              ? $t('LAUNCH_CAMPAIGN_LAUNCH')
              : $t('LAUNCH_CAMPAIGN_SCHEDULE')
          }}
        </DtButton>
      </div>
    </div>
  </DtModal>
</template>
